// Industry-standard emission factors & assumptions (illustrative, cite-ready)
// Sources: India CEA Grid Emission Factor (0.716 tCO2/MWh), GHG Protocol, IPCC AR6, CBAM 2026 defaults.

export const FACTORS = {
    // tCO2e per unit
    india_grid_kwh: 0.000716, // tCO2/kWh (Scope 2)
    diesel_litre: 0.00268, // tCO2/L (Scope 1)
    lpg_kg: 0.00299, // tCO2/kg (Scope 1)
    // Prices
    cbam_eur_per_t: 85, // 2026 default price
    inr_per_eur: 90, // approx
    voluntary_offset_inr_per_t: 800, // avg VCM
    compliance_offset_inr_per_t: 3200, // article 6 / compliance grade
    // Cost of capital
    coc_bps_per_intensity_unit: 12, // bps uplift per tCO2/Cr revenue intensity unit
    // Scope weighting for composite risk (must sum to 1)
    scope_weights: { s1: 0.35, s2: 0.25, s3: 0.4 }
};

// Persona baselines (illustrative typical values)
export const PERSONAS = {
    msme: {
        key: "msme",
        label: "MSME Exporter",
        subtitle: "Scope: Single-entity view · Cement / Steel exporter",
        baseline: {
            revenueCr: 120, // ₹ Cr
            exportShareEU: 45, // % to EU (CBAM-covered)
            s1: 8400, // tCO2e/yr
            s2: 3600,
            s3: 12800
        }
    },
    bank: {
        key: "bank",
        label: "Bank / NBFC",
        subtitle: "Scope: Financed emissions across MSME portfolio",
        baseline: {
            portfolioBookCr: 8500, // ₹ Cr AUM to carbon-intensive MSMEs
            borrowers: 620,
            s1: 620000, // financed tCO2e/yr
            s2: 240000,
            s3: 890000
        }
    },
    regulator: {
        key: "regulator",
        label: "Regulator",
        subtitle: "Scope: Sector-wide targets & benchmarks",
        baseline: {
            sectorGDPCr: 240000, // ₹ Cr sector output
            registeredEntities: 4200,
            s1: 12500000, // sector-wide tCO2e/yr
            s2: 6800000,
            s3: 21400000
        }
    }
};

// Compute simulated results given persona + reduction targets (0-70% each).
// `overrides` may supply a live Scope 2 baseline (annual tCO2e) from a real feed.
export function simulate(personaKey, targets, overrides = {}) {
    const p = PERSONAS[personaKey];
    const s1 = p.baseline.s1;
    const s2 = overrides.s2 != null ? overrides.s2 : p.baseline.s2;
    const s3 = p.baseline.s3;

    const rs1 = s1 * (1 - targets.s1 / 100);
    const rs2 = s2 * (1 - targets.s2 / 100);
    const rs3 = s3 * (1 - targets.s3 / 100);
    const totalReduced = rs1 + rs2 + rs3;
    const totalBaseline = s1 + s2 + s3;
    const reductionPct = ((totalBaseline - totalReduced) / totalBaseline) * 100;

    // Composite risk score 0-100 (higher = riskier). Baseline calibrated to ~72.
    const baseIntensity =
        personaKey === "msme"
            ? totalBaseline / Math.max(p.baseline.revenueCr, 1)
            : personaKey === "bank"
            ? totalBaseline / Math.max(p.baseline.portfolioBookCr, 1)
            : totalBaseline / Math.max(p.baseline.sectorGDPCr, 1);
    const simIntensity =
        personaKey === "msme"
            ? totalReduced / Math.max(p.baseline.revenueCr, 1)
            : personaKey === "bank"
            ? totalReduced / Math.max(p.baseline.portfolioBookCr, 1)
            : totalReduced / Math.max(p.baseline.sectorGDPCr, 1);

    const baselineScore = clamp(
        Math.round(
            30 +
                FACTORS.scope_weights.s1 * (s1 / totalBaseline) * 90 +
                FACTORS.scope_weights.s2 * (s2 / totalBaseline) * 80 +
                FACTORS.scope_weights.s3 * (s3 / totalBaseline) * 100
        ),
        20,
        95
    );
    const scoreDrop = reductionPct * 0.55; // 1% reduction ≈ 0.55 pts
    const simulatedScore = clamp(Math.round(baselineScore - scoreDrop), 5, 100);

    // Financial impact — INR Cr / yr
    const cbamSavingsInrCr =
        personaKey === "msme"
            ? ((totalBaseline - totalReduced) *
                  (p.baseline.exportShareEU / 100) *
                  FACTORS.cbam_eur_per_t *
                  FACTORS.inr_per_eur) /
              1e7
            : personaKey === "bank"
            ? ((totalBaseline - totalReduced) * 0.18 * FACTORS.cbam_eur_per_t * FACTORS.inr_per_eur) / 1e7
            : ((totalBaseline - totalReduced) * 0.22 * FACTORS.cbam_eur_per_t * FACTORS.inr_per_eur) / 1e7;

    const cocBpsSavings = Math.min(320, (baseIntensity - simIntensity) * FACTORS.coc_bps_per_intensity_unit);
    const cocInrCr =
        personaKey === "msme"
            ? (cocBpsSavings / 10000) * p.baseline.revenueCr * 0.6 // debt ~60% of revenue
            : personaKey === "bank"
            ? (cocBpsSavings / 10000) * p.baseline.portfolioBookCr
            : (cocBpsSavings / 10000) * p.baseline.sectorGDPCr * 0.3;

    const totalSavingsInrCr = cbamSavingsInrCr + cocInrCr;

    // 5-year projection points
    const years = [2025, 2026, 2027, 2028, 2029, 2030];
    const projection = years.map((y, i) => {
        const t = i / (years.length - 1);
        const ease = t * t * (3 - 2 * t); // smoothstep
        return {
            year: y,
            baselineScore,
            simulatedScore: Math.round(baselineScore - scoreDrop * ease),
            baselineCost: Number(((totalBaseline * (personaKey === "msme" ? p.baseline.exportShareEU / 100 : 0.2) * FACTORS.cbam_eur_per_t * FACTORS.inr_per_eur) / 1e7).toFixed(1)),
            savings: Number((totalSavingsInrCr * ease).toFixed(1))
        };
    });

    return {
        baselineScore,
        simulatedScore,
        reductionPct: Number(reductionPct.toFixed(1)),
        totalReduced: Math.round(totalReduced),
        totalBaseline: Math.round(totalBaseline),
        cbamSavingsInrCr: Number(cbamSavingsInrCr.toFixed(2)),
        cocBpsSavings: Math.round(cocBpsSavings),
        cocInrCr: Number(cocInrCr.toFixed(2)),
        totalSavingsInrCr: Number(totalSavingsInrCr.toFixed(2)),
        projection
    };
}

function clamp(v, lo, hi) {
    return Math.max(lo, Math.min(hi, v));
}

// Regulator target storage (localStorage) — MSME/Bank read these as benchmarks
export const TARGETS_KEY = "carbonis:regulator-targets";
export const SCENARIOS_KEY = "carbonis:scenarios";

export function loadTargets() {
    if (typeof window === "undefined") return { s1: 30, s2: 40, s3: 25, year: 2030 };
    try {
        return JSON.parse(localStorage.getItem(TARGETS_KEY)) || { s1: 30, s2: 40, s3: 25, year: 2030 };
    } catch {
        return { s1: 30, s2: 40, s3: 25, year: 2030 };
    }
}

export function saveTargets(t) {
    localStorage.setItem(TARGETS_KEY, JSON.stringify(t));
}

export function loadScenarios(personaKey) {
    if (typeof window === "undefined") return [];
    try {
        const all = JSON.parse(localStorage.getItem(SCENARIOS_KEY)) || {};
        return all[personaKey] || [];
    } catch {
        return [];
    }
}

export function saveScenario(personaKey, scenario) {
    const all = (() => {
        try {
            return JSON.parse(localStorage.getItem(SCENARIOS_KEY)) || {};
        } catch {
            return {};
        }
    })();
    all[personaKey] = [scenario, ...(all[personaKey] || [])].slice(0, 8);
    localStorage.setItem(SCENARIOS_KEY, JSON.stringify(all));
    return all[personaKey];
}

export function deleteScenario(personaKey, id) {
    const all = (() => {
        try {
            return JSON.parse(localStorage.getItem(SCENARIOS_KEY)) || {};
        } catch {
            return {};
        }
    })();
    all[personaKey] = (all[personaKey] || []).filter((s) => s.id !== id);
    localStorage.setItem(SCENARIOS_KEY, JSON.stringify(all));
    return all[personaKey];
}
